import React, {
  useEffect, useMemo, useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';

import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import { getLocale } from '@edx/frontend-platform/i18n';
import { Form, Spinner, StatefulButton } from '@openedx/paragon';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import Cookies from 'universal-cookie';

import ConfigurableRegistrationForm from './components/ConfigurableRegistrationForm';
import RegistrationFailure from './components/RegistrationFailure';
import {
  backupRegistrationFormBegin,
  clearRegistrationBackendError,
  registerNewUser,
  setEmailSuggestionInStore,
  setUserPipelineDataLoaded
} from './data/actions';
import {
  FORM_SUBMISSION_ERROR,
  TPA_AUTHENTICATION_FAILURE
} from './data/constants';
import getBackendValidations from './data/selectors';
import {
  isFormValid, prepareRegistrationPayload
} from './data/utils';
import messages from './messages';
import { EmailField, NameField, UsernameField } from './RegistrationFields';
import {
  InstitutionLogistration,
  PasswordField,
  RedirectLogistration,
  ThirdPartyAuthAlert
} from '../common-components';
import { getThirdPartyAuthContext as getRegistrationDataFromBackend } from '../common-components/data/actions';
import EnterpriseSSO from '../common-components/EnterpriseSSO';
import {
  COMPLETE_STATE, LOGIN_PAGE, PENDING_STATE, REGISTER_PAGE
} from '../data/constants';
import {
  getAllPossibleQueryParams, getTpaHint, getTpaProvider, isHostAvailableInQueryParams, setCookie, updatePathWithQueryParams
} from '../data/utils';

import authBG from '../assets/authBG.jpg';
import logo from '../assets/logo.png';
/* eslint-disable-next-line global-require */
const arMessages = require("../translations/ar.js");
import { useT } from '../context/LanguageContext';

// Microsoft Icon SVG
const MicrosoftIcon = () => (
  <svg className="microsoft-icon" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#008EC3" />
    <rect x="11" y="1" width="9" height="9" fill="#008EC3" />
    <rect x="1" y="11" width="9" height="9" fill="#008EC3" />
    <rect x="11" y="11" width="9" height="9" fill="#008EC3" />
  </svg>
);

// Google Icon SVG
const GoogleIcon = () => (
  <svg className="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

// Azure Icon SVG
const AzureIcon = () => (
  <svg className="azure-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.05 4.24L6.56 18.05L2 18.41L9.11 4.24H13.05ZM13.75 5.88L22 18.41H17.04L13.75 5.88ZM7.09 8.46L11.62 14.38L11.25 18.12L3.32 18.41L7.09 8.46Z" fill="#0089D6" />
  </svg>
);

/**
 * Main Registration Page component
 */
const RegistrationPage = (props) => {
  const { t, isArabic, toggleLang } = useT();
  const dispatch = useDispatch();

  const registrationEmbedded = isHostAvailableInQueryParams();
  const platformName = getConfig().SITE_NAME;
  const flags = {
    showConfigurableEdxFields: getConfig().SHOW_CONFIGURABLE_EDX_FIELDS,
    showConfigurableRegistrationFields: getConfig().ENABLE_DYNAMIC_REGISTRATION_FIELDS,
    showMarketingEmailOptInCheckbox: getConfig().MARKETING_EMAILS_OPT_IN,
    autoGeneratedUsernameEnabled: getConfig().ENABLE_AUTO_GENERATED_USERNAME
  };
  const { institutionLogin } = props;

  const backedUpFormData = useSelector(state => state.register.registrationFormData) || {};
  const registrationError = useSelector(state => state.register.registrationError) || {};
  const registrationErrorCode = registrationError?.errorCode;
  const registrationResult = useSelector(state => state.register.registrationResult) || {};
  const shouldBackupState = useSelector(state => state.register.shouldBackupState);
  const userPipelineDataLoaded = useSelector(state => state.register.userPipelineDataLoaded);
  const submitState = useSelector(state => state.register.submitState);

  const fieldDescriptions = useSelector(state => state.commonComponents.fieldDescriptions) || {};
  const optionalFields = useSelector(state => state.commonComponents.optionalFields) || { fields: {} };
  const thirdPartyAuthApiStatus = useSelector(state => state.commonComponents.thirdPartyAuthApiStatus);
  const thirdPartyAuthContext = useSelector(state => state.commonComponents.thirdPartyAuthContext) || {};
  const autoSubmitRegForm = thirdPartyAuthContext.autoSubmitRegForm;
  const thirdPartyAuthErrorMessage = thirdPartyAuthContext.errorMessage;
  const finishAuthUrl = thirdPartyAuthContext.finishAuthUrl;
  const currentProvider = thirdPartyAuthContext.currentProvider;
  const providers = thirdPartyAuthContext.providers || [];
  const secondaryProviders = thirdPartyAuthContext.secondaryProviders || [];
  const pipelineUserDetails = thirdPartyAuthContext.pipelineUserDetails || {};

  const backendValidations = useSelector(getBackendValidations);
  const queryParams = useMemo(() => getAllPossibleQueryParams(), []);
  const tpaHint = useMemo(() => getTpaHint(), []);

  // Default form values
  const defaultFormFields = {
    name: '', name_ar: '', email: '', username: '', password: ''
  };
  const defaultErrors = {};

  const [formFields, setFormFields] = useState({ ...defaultFormFields, ...(backedUpFormData.formFields || {}) });
  const [configurableFormFields, setConfigurableFormFields] = useState({ ...(backedUpFormData.configurableFormFields || {}) });
  const [errors, setErrors] = useState({ ...defaultErrors, ...(backedUpFormData.errors || {}) });
  const [errorCode, setErrorCode] = useState({ type: '', count: 0 });
  const [formStartTime, setFormStartTime] = useState(null);
  // temporary error state for embedded experience because we don't want to show errors on blur
  const [temporaryErrors, setTemporaryErrors] = useState({ ...defaultErrors, ...(backedUpFormData.errors || {}) });

  const { cta, host } = queryParams;
  const buttonLabel = cta
    ? t('create.account.cta.button', { label: cta })
    : t('create.account.for.free.button');

  useEffect(() => {
    if (isArabic) {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'en');
    }
  }, [isArabic]);

  /**
   * Set the userPipelineDetails data in formFields for only first time
   */
  useEffect(() => {
    if (!userPipelineDataLoaded && thirdPartyAuthApiStatus === COMPLETE_STATE) {
      if (thirdPartyAuthErrorMessage) {
        setErrorCode(prevState => ({ type: TPA_AUTHENTICATION_FAILURE, count: prevState.count + 1 }));
      }
      if (pipelineUserDetails && Object.keys(pipelineUserDetails).length !== 0) {
        const { name = '', username = '', email = '' } = pipelineUserDetails;
        setFormFields(prevState => ({
          ...prevState, name, username, email
        }));
        dispatch(setUserPipelineDataLoaded(true));
      }
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    thirdPartyAuthApiStatus,
    thirdPartyAuthErrorMessage,
    pipelineUserDetails,
    userPipelineDataLoaded,
  ]);

  useEffect(() => {
    if (!formStartTime) {
      sendPageEvent('login_and_registration', 'register');
      const payload = { ...queryParams, is_register_page: true };
      if (tpaHint) {
        payload.tpa_hint = tpaHint;
      }
      dispatch(getRegistrationDataFromBackend(payload));
      setFormStartTime(Date.now());
    }
  }, [dispatch, formStartTime, queryParams, tpaHint]);

  /**
   * Backup the registration form in redux when register page is toggled.
   */
  useEffect(() => {
    if (shouldBackupState) {
      dispatch(backupRegistrationFormBegin({
        ...backedUpFormData,
        configurableFormFields: { ...configurableFormFields },
        formFields: { ...formFields },
        errors: { ...errors }
      }));
    }
  }, [shouldBackupState, configurableFormFields, formFields, errors, dispatch, backedUpFormData]);

  useEffect(() => {
    if (backendValidations) {
      if (registrationEmbedded) {
        setTemporaryErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      } else {
        setErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      }
    }
  }, [backendValidations, registrationEmbedded]);

  useEffect(() => {
    if (registrationErrorCode) {
      setErrorCode(prevState => ({ type: registrationErrorCode, count: prevState.count + 1 }));
    }
  }, [registrationErrorCode]);

  useEffect(() => {
    if (registrationResult.success) {
      // This event is used by GTM
      sendTrackEvent('edx.bi.user.account.registered.client', {});

      // This is used by the "User Retention Rate Event" on GTM
      setCookie(getConfig().USER_RETENTION_COOKIE_NAME, true);
    }
  }, [registrationResult]);

  const handleOnChange = (event) => {
    const { name } = event.target;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (registrationError[name]) {
      dispatch(clearRegistrationBackendError(name));
    }
    setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    setFormFields(prevState => ({ ...prevState, [name]: value }));
  };

  const handleErrorChange = (fieldName, error) => {
    if (registrationEmbedded) {
      setTemporaryErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: error
      }));
      if (error === '' && errors[fieldName] !== '') {
        setErrors(prevErrors => ({
          ...prevErrors,
          [fieldName]: error
        }));
      }
    } else {
      setErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: error
      }));
    }
  };

  const registerUser = () => {
    const totalRegistrationTime = (Date.now() - formStartTime) / 1000;
    let payload = { ...formFields };

    if (currentProvider) {
      delete payload.password;
      payload.social_auth_provider = currentProvider;
    }
    if (flags.autoGeneratedUsernameEnabled) {
      delete payload.username;
    }

    // Validating form data before submitting
    const { isValid, fieldErrors, emailSuggestion } = isFormValid(
      payload,
      registrationEmbedded ? temporaryErrors : errors,
      configurableFormFields,
      fieldDescriptions,
      t,
    );
    setErrors({ ...fieldErrors });
    dispatch(setEmailSuggestionInStore(emailSuggestion));

    // returning if not valid
    if (!isValid) {
      setErrorCode(prevState => ({ type: FORM_SUBMISSION_ERROR, count: prevState.count + 1 }));
      return;
    }

    // Preparing payload for submission
    payload = prepareRegistrationPayload(
      payload,
      configurableFormFields,
      flags.showMarketingEmailOptInCheckbox,
      totalRegistrationTime,
      queryParams);

    // making register call
    dispatch(registerNewUser(payload));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    registerUser();
  };

  useEffect(() => {
    if (autoSubmitRegForm && userPipelineDataLoaded) {
      registerUser();
    }
  }, [autoSubmitRegForm, userPipelineDataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderForm = () => {
    if (institutionLogin) {
      return (
        <InstitutionLogistration
          secondaryProviders={secondaryProviders}
          headingTitle={t('register.institution.login.page.title')}
        />
      );
    }
    return (
      <>
        <Helmet>
          <title>{t('register.page.title', { siteName: getConfig().SITE_NAME })}</title>
        </Helmet>
        <RedirectLogistration
          host={host}
          authenticatedUser={registrationResult.authenticatedUser}
          success={registrationResult.success}
          redirectUrl={registrationResult.redirectUrl}
          finishAuthUrl={finishAuthUrl}
          optionalFields={optionalFields}
          registrationEmbedded={registrationEmbedded}
          redirectToProgressiveProfilingPage={
            getConfig().ENABLE_PROGRESSIVE_PROFILING_ON_AUTHN && !!Object.keys(optionalFields?.fields || {}).length
          }
        />

        <div
          className="ksu-login-page"
          style={{
            backgroundImage: `url(${authBG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Language Switcher */}
          <div className="ksu-lang-switcher">
            <button type="button" className="lang-btn" onClick={toggleLang}>
              {isArabic ? 'EN' : 'العربية'}
            </button>
          </div>

          {/* Header with Logo */}
          <header className="ksu-header">
            <div className="ksu-logo-container">
              <a href="/">
                <img
                  src={logo}
                  alt="Platform Logo"
                  className="ksu-logo"
                />
              </a>
            </div>
          </header>

          {/* Main Content Card */}
          <main className="ksu-main-content">
            <div className="ksu-login-card">
              <h1 className="ksu-login-title">
                {t('register.page.title', { siteName: '' }).replace(' | ', '')}
              </h1>

              <div className="ksu-register-link">
                {isArabic ? 'لديك حساب بالفعل؟' : 'Already have an account?'}{' '}
                <Link to={updatePathWithQueryParams(LOGIN_PAGE)}>
                  {isArabic ? 'سجّل الدخول' : 'Sign in'}
                </Link>
              </div>
              {autoSubmitRegForm && !errorCode.type ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" id="tpa-spinner" />
                </div>
              ) : (
                <>
                  <ThirdPartyAuthAlert
                    currentProvider={currentProvider}
                    platformName={platformName}
                    referrer={REGISTER_PAGE}
                  />
                  <RegistrationFailure
                    errorCode={errorCode.type}
                    failureCount={errorCode.count}
                    context={{ provider: currentProvider, errorMessage: thirdPartyAuthErrorMessage }}
                  />
                  <Form id="registration-form" name="registration-form" className="ksu-form">
                    <div className="ksu-form-group">
                      <NameField
                        name="name"
                        value={formFields.name}
                        shouldFetchUsernameSuggestions={!formFields.username.trim()}
                        handleChange={handleOnChange}
                        handleErrorChange={handleErrorChange}
                        errorMessage={errors.name}
                        helpText={[t('help.text.name')]}
                        floatingLabel={t('registration.fullname.label')}
                      />
                    </div>
                    <div className="ksu-form-group">
                      <NameField
                        name="name_ar"
                        value={formFields.name_ar}
                        handleChange={handleOnChange}
                        handleErrorChange={handleErrorChange}
                        errorMessage={errors.name_ar}
                        helpText={[t('help.text.name.ar')]}
                        floatingLabel={t('registration.fullname.ar.label')}
                      />
                    </div>
                    <div className="ksu-form-group">
                      <EmailField
                        name="email"
                        value={formFields.email}
                        confirmEmailValue={configurableFormFields?.confirm_email}
                        handleErrorChange={handleErrorChange}
                        handleChange={handleOnChange}
                        errorMessage={errors.email}
                        helpText={[t('help.text.email')]}
                        floatingLabel={t('registration.email.label')}
                      />
                    </div>
                    {!flags.autoGeneratedUsernameEnabled && (
                      <div className="ksu-form-group">
                        <UsernameField
                          name="username"
                          spellCheck="false"
                          value={formFields.username}
                          handleChange={handleOnChange}
                          handleErrorChange={handleErrorChange}
                          errorMessage={errors.username}
                          helpText={[t('help.text.username.1'), t('help.text.username.2')]}
                          floatingLabel={t('registration.username.label')}
                        />
                      </div>
                    )}
                    {!currentProvider && (
                      <div className="ksu-form-group">
                        <PasswordField
                          name="password"
                          value={formFields.password}
                          handleChange={handleOnChange}
                          handleErrorChange={handleErrorChange}
                          errorMessage={errors.password}
                          floatingLabel={t('registration.password.label')}
                        />
                      </div>
                    )}
                    <ConfigurableRegistrationForm
                      email={formFields.email}
                      fieldErrors={errors}
                      formFields={configurableFormFields}
                      setFieldErrors={registrationEmbedded ? setTemporaryErrors : setErrors}
                      setFormFields={setConfigurableFormFields}
                      autoSubmitRegisterForm={autoSubmitRegForm}
                      fieldDescriptions={fieldDescriptions}
                    />
                    <StatefulButton
                      id="register-user"
                      name="register-user"
                      type="submit"
                      variant="brand"
                      className="ksu-login-btn mt-4"
                      state={submitState}
                      labels={{
                        default: buttonLabel,
                        pending: ''
                      }}
                      onClick={handleSubmit}
                      onMouseDown={(e) => e.preventDefault()}
                    />
                  </Form>
                </>
              )}
            </div>
          </main>

          {/* Social Login Section - Outside the card */}
          {!registrationEmbedded && (providers?.length > 0 || secondaryProviders?.length > 0) && (
            <section className="ksu-social-login">
              <p className="ksu-social-title">
                {t('registration.other.options.heading')}
              </p>
              {/* Primary Providers (e.g., KSU SSO) */}
              {providers?.length > 0 && (
                <div className="ksu-social-buttons">
                  {providers.map((provider) => (
                    <button
                      key={provider.id}
                      type="button"
                      className="ksu-social-btn"
                      onClick={() => {
                        window.location.href = getConfig().LMS_BASE_URL + provider.registerUrl;
                      }}
                    >
                      {provider.id.includes('azuread') && <MicrosoftIcon />}
                      {provider.name}
                    </button>
                  ))}
                </div>
              )}
              {/* Secondary Providers (e.g., Google, Azure) */}
              {secondaryProviders?.length > 0 && (
                <div className="ksu-social-buttons ksu-social-buttons-secondary">
                  {secondaryProviders.map((provider) => {
                    const isGoogle = provider.name.toLowerCase().includes('google');
                    const isAzure = provider.name.toLowerCase().includes('azure');
                    return (
                      <button
                        key={provider.id}
                        type="button"
                        className={`ksu-social-btn ${isGoogle ? 'ksu-social-btn-google' : ''} ${isAzure ? 'ksu-social-btn-azure' : ''}`}
                        onClick={() => {
                          window.location.href = getConfig().LMS_BASE_URL + provider.registerUrl;
                        }}
                      >
                        {isGoogle && <GoogleIcon />}
                        {isAzure && <AzureIcon />}
                        {!isGoogle && !isAzure && <MicrosoftIcon />}
                        {isArabic ? `التسجيل بواسطة ${provider.name}` : `Register with ${provider.name}`}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>
      </>
    );
  };

  if (tpaHint) {
    if (thirdPartyAuthApiStatus === PENDING_STATE) {
      return <Skeleton height={36} />;
    }
    const { provider, skipHintedLogin } = getTpaProvider(tpaHint, providers, secondaryProviders);
    if (skipHintedLogin) {
      window.location.href = getConfig().LMS_BASE_URL + provider.registerUrl;
      return null;
    }
    return provider ? <EnterpriseSSO provider={provider} /> : renderForm();
  }
  return (
    renderForm()
  );
};

RegistrationPage.propTypes = {
  institutionLogin: PropTypes.bool,
  // Actions
  handleInstitutionLogin: PropTypes.func
};

RegistrationPage.defaultProps = {
  handleInstitutionLogin: null,
  institutionLogin: false
};

// Wrapper component with IntlProvider for Arabic locale
const RegistrationPageWrapper = (props) => {
  const cookies = new Cookies();
  const cookieName = getConfig().LANGUAGE_PREFERENCE_COOKIE_NAME || 'openedx-language-preference';

  // Get locale from URL, cookie, or localStorage and sync storage
  const getLocaleValue = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLocale = urlParams.get('lang');

    // If URL has locale, use it and sync to storage
    if (urlLocale && ['ar', 'en'].includes(urlLocale)) {
      // Sync URL locale to cookie and localStorage
      const hostname = window.location.hostname;
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
      const cookieOptions = {
        path: '/',
        maxAge: 31536000,
        sameSite: 'lax'
      };
      if (!isLocalhost) {
        const domainParts = hostname.split('.');
        if (domainParts.length >= 2) {
          cookieOptions.domain = `.${domainParts.slice(-2).join('.')}`;
        }
        if (window.location.protocol === 'https:') {
          cookieOptions.secure = true;
        }
      }
      cookies.set(cookieName, urlLocale, cookieOptions);
      localStorage.setItem(cookieName, urlLocale);
      return urlLocale;
    }

    // Then check cookie
    const cookieLocale = cookies.get(cookieName);
    if (cookieLocale && ['ar', 'en'].includes(cookieLocale)) {
      return cookieLocale;
    }

    // Then localStorage
    const localStorageLocale = localStorage.getItem(cookieName);
    if (localStorageLocale && ['ar', 'en'].includes(localStorageLocale)) {
      return localStorageLocale;
    }

    return 'en';
  };

  const locale = getLocaleValue();

  if (locale === 'ar') {
    return (
      <IntlProvider locale="ar" messages={arMessages}>
        <RegistrationPage {...props} />
      </IntlProvider>
    );
  }

  return <RegistrationPage {...props} />;
};

RegistrationPageWrapper.propTypes = RegistrationPage.propTypes;
RegistrationPageWrapper.defaultProps = RegistrationPage.defaultProps;

export default RegistrationPageWrapper;
